/**
 * ReplicationBinaryCodec - low-level binary encode/decode for replication payloads.
 *
 * Provides BinaryWriter, BinaryReader, a FNV-1a class hash, and helpers for
 * writing/reading class discriminators used by polymorphic `class`-typed properties.
 *
 * All multi-byte values are written in little-endian order.
 * The stream is bit-level: no padding is inserted between fields. Boolean flags,
 * presence bits, bitmasks, and small enums occupy only the bits they need.
 */
import { ClassRegistry } from '../systems/ClassRegistry.js';

// ---------------------------------------------------------------------------
// Shared reusable DataView for float ↔ int reinterpretation (no per-call alloc)
// ---------------------------------------------------------------------------

const _tmpBuf = new ArrayBuffer(8);
const _tmpView = new DataView(_tmpBuf);
const _encoder = new TextEncoder();
const _decoder = new TextDecoder();

// ---------------------------------------------------------------------------
// BinaryWriter
// ---------------------------------------------------------------------------

const INITIAL_CAPACITY = 256;

/**
 * Growable bit-level binary write buffer.
 *
 * Uses a 32-bit accumulator (`_bitAccum`) to hold bits that have not yet been
 * committed to the underlying `ArrayBuffer`. Bits are packed LSB-first.
 *
 * Call {@link BinaryWriter.getBytes} after all writes to obtain the final `Uint8Array`.
 */
export class BinaryWriter {
  private buffer: ArrayBuffer;
  private view: DataView;
  /** Next byte offset to write committed data into. */
  private _offset: number = 0;
  /** Pending bits not yet committed to the buffer (LSB-first, unsigned 32-bit). */
  private _bitAccum: number = 0;
  /** Number of valid bits currently in `_bitAccum` (0–31). */
  private _bitsStored: number = 0;

  constructor(initialCapacity = INITIAL_CAPACITY) {
    this.buffer = new ArrayBuffer(initialCapacity);
    this.view = new DataView(this.buffer);
  }

  /** Current write position as a bit offset (byte offset × 8 + pending bits). */
  get bitCursor(): number { return this._offset * 8 + this._bitsStored; }

  private grow(needed: number): void {
    const required = this._offset + needed;
    if (required <= this.buffer.byteLength) return;
    let newSize = this.buffer.byteLength * 2;
    while (newSize < required) newSize *= 2;
    const newBuffer = new ArrayBuffer(newSize);
    new Uint8Array(newBuffer).set(new Uint8Array(this.buffer));
    this.buffer = newBuffer;
    this.view = new DataView(this.buffer);
  }

  /**
   * Core internal write primitive.
   *
   * Merges `count` LSB-first bits from `v` into the 32-bit accumulator.
   * - If the bits fit (`_bitsStored + count ≤ 32`): OR them in; if the accumulator
   *   reaches exactly 32 bits, flush it as a single `setUint32`.
   * - Otherwise: fill the accumulator with the low `(32 - _bitsStored)` bits of `v`
   *   (JS 32-bit truncation handles masking naturally), flush one `setUint32`, then
   *   place the remaining high bits in the fresh accumulator.
   *
   * `count` must be 1–32.
   */
  private _writeBitsToAccum(v: number, count: number): void {
    // Mask v to count bits to prevent sign-extension from corrupting adjacent packed fields.
    const masked = count < 32 ? (v & ((1 << count) - 1)) : (v >>> 0);
    const room = 32 - this._bitsStored;
    if (count <= room) {
      this._bitAccum = this._bitAccum | (masked << this._bitsStored);
      this._bitsStored += count;
      if (this._bitsStored === 32) {
        this.grow(4);
        this.view.setUint32(this._offset, this._bitAccum, true);
        this._offset += 4;
        this._bitAccum = 0;
        this._bitsStored = 0;
      }
    } else {
      // Fill the accumulator with the low `room` bits of masked, then flush.
      this._bitAccum = this._bitAccum | (masked << this._bitsStored);
      this.grow(4);
      this.view.setUint32(this._offset, this._bitAccum, true);
      this._offset += 4;
      // Remaining high bits start the new accumulator.
      this._bitAccum = masked >>> room;
      this._bitsStored = count - room;
    }
  }

  /** Write a single bit. */
  writeBit(v: boolean): void {
    this._writeBitsToAccum(v ? 1 : 0, 1);
  }

  /**
   * Write `count` LSB-first bits from `v`.
   * `count` must be 1–32.
   */
  writeBits(v: number, count: number): void {
    this._writeBitsToAccum(v, count);
  }

  writeBool(v: boolean): void {
    this._writeBitsToAccum(v ? 1 : 0, 1);
  }

  writeUint8(v: number): void {
    this._writeBitsToAccum(v, 8);
  }

  writeInt8(v: number): void {
    this._writeBitsToAccum(v, 8);
  }

  writeUint16(v: number): void {
    this._writeBitsToAccum(v, 16);
  }

  writeInt16(v: number): void {
    this._writeBitsToAccum(v, 16);
  }

  writeInt32(v: number): void {
    this._writeBitsToAccum(v, 32);
  }

  writeUint32(v: number): void {
    this._writeBitsToAccum(v, 32);
  }

  /**
   * Write an unsigned integer using variable-length encoding.
   * Each group encodes 7 value bits (LSB-first) followed by a 1-bit continuation flag.
   * Values 0–127 cost 8 bits; 128–16383 cost 16 bits; and so on.
   * No byte alignment is required — bits are packed directly into the stream.
   */
  writeVaruint(v: number): void {
    v = v >>> 0;
    while (true) {
      const group = v & 0x7F;
      v >>>= 7;
      this._writeBitsToAccum(v > 0 ? (group | 0x80) : group, 8);
      if (v === 0) break;
    }
  }

  /**
   * Write a signed integer using zigzag + variable-length encoding.
   * Maps `n` → `(n << 1) ^ (n >> 31)` to make small-magnitude values cheap,
   * then encodes the result with {@link writeVaruint}.
   */
  writeVarsint(v: number): void {
    this.writeVaruint(((v << 1) ^ (v >> 31)) >>> 0);
  }

  writeFloat16(v: number): void {
    this._writeBitsToAccum(floatToFloat16Bits(v), 16);
  }

  writeFloat32(v: number): void {
    _tmpView.setFloat32(0, v, true);
    this._writeBitsToAccum(_tmpView.getUint32(0, true), 32);
  }

  writeFloat64(v: number): void {
    _tmpView.setFloat64(0, v, true);
    this._writeBitsToAccum(_tmpView.getUint32(0, true), 32);  // low word
    this._writeBitsToAccum(_tmpView.getUint32(4, true), 32);  // high word
  }

  /** Write a UTF-8 string prefixed with a varuint byte length. */
  writeString(v: string): void {
    const bytes = _encoder.encode(v);
    this.writeVaruint(bytes.byteLength);
    for (let i = 0; i < bytes.byteLength; i++) {
      this._writeBitsToAccum(bytes[i], 8);
    }
  }

  /**
   * Write an actor ID as raw hex bytes.
   * Encodes the length as a 2-bit tag: 0 = null/invalid, 1 = 8-byte shortUUID, 2 = 16-byte legacy UUID.
   */
  writeActorId(v: string): void {
    const hex = v.replace(/-/g, '').toLowerCase();
    const byteLength = hex.length / 2;
    if ((byteLength !== 8 && byteLength !== 16) || !/^[0-9a-f]+$/.test(hex)) {
      this._writeBitsToAccum(0, 2); // null tag
      return;
    }

    this._writeBitsToAccum(byteLength === 8 ? 1 : 2, 2);
    for (let i = 0; i < byteLength; i++) {
      this._writeBitsToAccum(parseInt(hex.slice(i * 2, i * 2 + 2), 16), 8);
    }
  }

  /**
   * Write a 1-bit presence flag: 0 = null, 1 = value follows.
   * Always call this before writing the actual value for nullable properties.
   */
  writePresence(present: boolean): void {
    this._writeBitsToAccum(present ? 1 : 0, 1);
  }

  /**
   * Write a bitmask indicating which property indices are present.
   * `count` is the total number of properties; `presentIndices` is the set of indices that changed.
   */
  writeBitmask(count: number, presentIndices: Set<number>): void {
    for (let i = 0; i < count; i++) {
      this._writeBitsToAccum(presentIndices.has(i) ? 1 : 0, 1);
    }
  }

  /**
   * Reserve `count` zero bits for a bitmask and return the bit cursor where they were written.
   * Call {@link patchBitmask} with that cursor once all changed indices are known.
   */
  reserveBitmask(count: number): number {
    const cursor = this.bitCursor;
    let remaining = count;
    while (remaining >= 32) {
      this._writeBitsToAccum(0, 32);
      remaining -= 32;
    }
    if (remaining > 0) this._writeBitsToAccum(0, remaining);
    return cursor;
  }

  /**
   * Overwrite a previously reserved bitmask at `atBitCursor`.
   * Handles bits that are either committed to the buffer or still in `_bitAccum`.
   */
  patchBitmask(atBitCursor: number, count: number, presentIndices: Set<number>): void {
    for (let i = 0; i < count; i++) {
      this._patchBit(atBitCursor + i, presentIndices.has(i));
    }
  }

  /**
   * Overwrite a single uint8 at a previously written bit cursor position.
   * The bit cursor must be byte-aligned (i.e. a multiple of 8).
   */
  patchUint8(atBitCursor: number, v: number): void {
    for (let i = 0; i < 8; i++) {
      this._patchBit(atBitCursor + i, ((v >>> i) & 1) !== 0);
    }
  }

  /** Overwrite a single bit at an absolute bit position. */
  private _patchBit(bitPos: number, v: boolean): void {
    const committedBits = this._offset * 8;
    if (bitPos >= committedBits) {
      // Bit is in the accumulator — patch in place.
      const accumIdx = bitPos - committedBits;
      if (v) this._bitAccum = this._bitAccum | (1 << accumIdx);
      else this._bitAccum = this._bitAccum & ~(1 << accumIdx);
    } else {
      // Bit is committed to the buffer — patch via DataView.
      const byteIdx = bitPos >> 3;
      const bitIdx = bitPos & 7;
      const cur = this.view.getUint8(byteIdx);
      this.view.setUint8(byteIdx, v ? (cur | (1 << bitIdx)) : (cur & ~(1 << bitIdx)));
    }
  }

  /**
   * Roll back the write cursor to a previously saved {@link bitCursor} value,
   * discarding all bits written after it.
   *
   * - If the target is within the current accumulator window (`targetByte >= _offset`):
   *   simply reduce `_bitsStored`; the accumulator's low bits are untouched because
   *   only OR operations ran within a single accumulator window.
   * - If the target byte is already committed to the buffer: restore `_offset`,
   *   `_bitsStored`, and reconstruct `_bitAccum` from the committed byte.
   */
  resetToBit(bitCursor: number): void {
    const targetByte = bitCursor >> 3;
    const targetBits = bitCursor & 7;
    if (targetByte >= this._offset) {
      // Within the accumulator — low bits are still intact.
      this._bitsStored = (targetByte - this._offset) * 8 + targetBits;
    } else {
      // Target byte is already committed.
      this._offset = targetByte;
      this._bitsStored = targetBits;
      this._bitAccum = targetBits > 0
        ? this.view.getUint8(targetByte) & ((1 << targetBits) - 1)
        : 0;
    }
  }

  /**
   * Flush any remaining bits in `_bitAccum` as 1–3 partial bytes.
   * Called internally by {@link getBytes}; writes only the bytes actually needed.
   */
  private _flushBits(): void {
    if (this._bitsStored === 0) return;
    const byteCount = Math.ceil(this._bitsStored / 8);
    this.grow(byteCount);
    let accum = this._bitAccum;
    for (let i = 0; i < byteCount; i++) {
      this.view.setUint8(this._offset++, accum & 0xFF);
      accum >>>= 8;
    }
    this._bitAccum = 0;
    this._bitsStored = 0;
  }

  /** Return a copy of the written bytes. Flushes any pending partial byte(s). */
  getBytes(): Uint8Array {
    this._flushBits();
    return new Uint8Array(this.buffer, 0, this._offset);
  }
}

// ---------------------------------------------------------------------------
// BinaryReader
// ---------------------------------------------------------------------------

/**
 * Sequential bit-level read cursor over a `Uint8Array`.
 *
 * Uses a 32-bit buffer (`_bitBuf`) to hold bits loaded from the stream but
 * not yet consumed. Multi-byte reads use a single `getUint32` and merge with
 * the buffer in O(1) — no per-byte loops on the hot path.
 */
export class BinaryReader {
  private view: DataView;
  private _offset: number;
  /** Bits loaded from the stream but not yet consumed (LSB-first). */
  private _bitBuf: number = 0;
  /** Number of valid bits in `_bitBuf` (0–31). */
  private _bitsLeft: number = 0;

  constructor(data: Uint8Array, startOffset = 0) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    this._offset = startOffset;
  }

  /** Byte length of the underlying data. */
  get byteLength(): number { return this.view.byteLength; }

  /**
   * Number of bytes remaining in the underlying buffer from the committed read
   * position. Does not account for bits already loaded into `_bitBuf`.
   * Suitable for coarse boundary checks only.
   */
  get remaining(): number { return this.view.byteLength - this._offset; }

  /**
   * Current read position as a bit offset.
   * Equals `_offset * 8 - _bitsLeft` (bytes consumed × 8 minus unconsumed buffered bits).
   */
  get bitCursor(): number { return this._offset * 8 - this._bitsLeft; }

  /**
   * Core internal read primitive.
   *
   * Reads `count` LSB-first bits from the stream.
   * - If enough bits are buffered in `_bitBuf`: extract them directly.
   * - Otherwise: load the next 4 bytes via a single `getUint32`, merge with the
   *   existing buffer, extract `count` bits, and retain the remainder.
   *
   * `count` must be 1–32.
   */
  private _readBitsFromBuf(count: number): number {
    if (count <= this._bitsLeft) {
      // Fast path: all bits already buffered.
      const v = this._bitBuf & ((count < 32 ? (1 << count) : 0) - 1);
      this._bitBuf >>>= count;
      this._bitsLeft -= count;
      return v;
    }

    // Need more bits from the stream.
    const needed = count - this._bitsLeft;
    const lo = this._bitBuf; // remaining buffered bits (all valid)
    const loBits = this._bitsLeft;

    // Load next 4 bytes (or fewer if near end — read byte-by-byte as fallback).
    let next = 0;
    const avail = this.view.byteLength - this._offset;
    if (avail >= 4) {
      next = this.view.getUint32(this._offset, true);
      this._offset += 4;
      this._bitsLeft = 32 - needed;
      // Guard against JS `>>> 32 = >>> 0` (modulo-32 shift): when needed = 32,
      // `next >>> 32` leaves _bitBuf = next instead of 0, corrupting the next read.
      this._bitBuf = needed < 32 ? (next >>> needed) : 0;
    } else {
      // Near end of stream — read available bytes one at a time.
      for (let i = 0; i < avail; i++) {
        next |= this.view.getUint8(this._offset++) << (i * 8);
      }
      this._bitsLeft = avail * 8 - needed;
      this._bitBuf = this._bitsLeft > 0 ? next >>> needed : 0;
    }

    const hi = next & ((needed < 32 ? (1 << needed) : 0) - 1);
    return (lo | (hi << loBits)) >>> 0;
  }

  /** Read a single bit. */
  readBit(): boolean {
    return this._readBitsFromBuf(1) !== 0;
  }

  /**
   * Read `count` LSB-first bits as an unsigned integer.
   * `count` must be 1–32.
   */
  readBits(count: number): number {
    return this._readBitsFromBuf(count);
  }

  readBool(): boolean {
    return this._readBitsFromBuf(1) !== 0;
  }

  readUint8(): number {
    return this._readBitsFromBuf(8);
  }

  readInt8(): number {
    const v = this._readBitsFromBuf(8);
    return v >= 0x80 ? v - 0x100 : v;
  }

  readUint16(): number {
    return this._readBitsFromBuf(16);
  }

  readInt16(): number {
    const v = this._readBitsFromBuf(16);
    return v >= 0x8000 ? v - 0x10000 : v;
  }

  readInt32(): number {
    return this._readBitsFromBuf(32) | 0;
  }

  readUint32(): number {
    return this._readBitsFromBuf(32);
  }

  /**
   * Read an unsigned variable-length integer written by {@link BinaryWriter.writeVaruint}.
   * Reads 8-bit groups (7 value bits + 1 continuation bit) until the continuation bit is 0.
   * Capped at 5 groups (35 value bits) to guard against malformed streams.
   */
  readVaruint(): number {
    let result = 0;
    let shift = 0;
    for (let i = 0; i < 5; i++) {
      const group = this._readBitsFromBuf(8);
      result = (result | ((group & 0x7F) << shift)) >>> 0;
      shift += 7;
      if (!(group & 0x80)) break;
    }
    return result;
  }

  /**
   * Read a signed variable-length integer written by {@link BinaryWriter.writeVarsint}.
   * Reads a {@link readVaruint} then reverses zigzag: `(n >>> 1) ^ -(n & 1)`.
   */
  readVarsint(): number {
    const n = this.readVaruint();
    return ((n >>> 1) ^ -(n & 1)) | 0;
  }

  readFloat16(): number {
    return float16BitsToFloat(this._readBitsFromBuf(16));
  }

  readFloat32(): number {
    _tmpView.setUint32(0, this._readBitsFromBuf(32), true);
    return _tmpView.getFloat32(0, true);
  }

  readFloat64(): number {
    _tmpView.setUint32(0, this._readBitsFromBuf(32), true); // low word
    _tmpView.setUint32(4, this._readBitsFromBuf(32), true); // high word
    return _tmpView.getFloat64(0, true);
  }

  /** Read a UTF-8 string prefixed with a varuint byte length. */
  readString(): string {
    const byteLength = this.readVaruint();
    const bytes = new Uint8Array(byteLength);
    for (let i = 0; i < byteLength; i++) {
      bytes[i] = this._readBitsFromBuf(8);
    }
    return _decoder.decode(bytes);
  }

  /**
   * Read an actor ID written by {@link BinaryWriter.writeActorId}.
   * The 2-bit tag selects: 0 = null, 1 = 8-byte shortUUID, 2 = 16-byte legacy UUID.
   * Returns shortUUIDs as 16 hex chars and legacy UUIDs in canonical dashed form.
   */
  readActorId(): string {
    const tag = this._readBitsFromBuf(2);
    if (tag === 0) return '';
    const byteLength = tag === 1 ? 8 : 16;

    const nibbles: string[] = [];
    for (let i = 0; i < byteLength; i++) {
      nibbles.push(this._readBitsFromBuf(8).toString(16).padStart(2, '0'));
    }
    const h = nibbles.join('');
    if (byteLength === 8) return h;
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }

  /** Skip an actor ID without decoding. */
  skipActorId(): void {
    const tag = this._readBitsFromBuf(2);
    if (tag === 0) return;
    const byteLength = tag === 1 ? 8 : 16;
    for (let i = 0; i < byteLength; i++) this._readBitsFromBuf(8);
  }

  /**
   * Read a 1-bit presence flag. Returns true if the value follows, false if null.
   */
  readPresence(): boolean {
    return this._readBitsFromBuf(1) !== 0;
  }

  /**
   * Read a bitmask and return which property indices are present.
   * `count` must match the value passed to {@link BinaryWriter.writeBitmask}.
   */
  readBitmask(count: number): boolean[] {
    const result = new Array<boolean>(count);
    for (let i = 0; i < count; i++) {
      result[i] = this._readBitsFromBuf(1) !== 0;
    }
    return result;
  }
}

// ---------------------------------------------------------------------------
// FNV-1a class hash (for polymorphic class discriminator)
// ---------------------------------------------------------------------------

/**
 * 32-bit FNV-1a hash of a string.
 * Used to encode class names compactly on the wire for polymorphic `class` properties.
 */
export function fnv1a32(str: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    // Multiply by FNV prime (32-bit: 0x01000193) using 32-bit unsigned arithmetic
    hash = (Math.imul(hash, 0x01000193) >>> 0);
  }
  return hash;
}

// Lazy map: hash → full registered class name (built once from ClassRegistry)
let classHashMap: Map<number, string> | null = null;

/**
 * Build (or return the cached) hash → class name map from the current ClassRegistry.
 * Called lazily on first polymorphic decode.
 */
export function getClassHashMap(): Map<number, string> {
  if (classHashMap) return classHashMap;
  classHashMap = new Map();
  for (const [fullName] of ClassRegistry.getRegistry()) {
    const hash = fnv1a32(fullName);
    if (classHashMap.has(hash)) {
      console.warn(`[ReplicationBinaryCodec] FNV-1a hash collision: classes "${classHashMap.get(hash)}" and "${fullName}" share hash ${hash}. One will be unreachable via polymorphic replication.`);
    }
    classHashMap.set(hash, fullName);
  }
  return classHashMap;
}

/**
 * Invalidate the class hash cache (call after ClassRegistry changes, e.g. game reload).
 */
export function invalidateClassHashMap(): void {
  classHashMap = null;
}

/**
 * Write a uint32 FNV-1a class discriminator for the given object instance.
 * Writes 0 if the class name cannot be resolved (treated as unknown on decode).
 */
export function writeClassDiscriminator(writer: BinaryWriter, instance: object): void {
  const fullName = ClassRegistry.getClassName(instance);
  if (!fullName) {
    writer.writeUint32(0);
    return;
  }
  writer.writeUint32(fnv1a32(fullName));
}

// ---------------------------------------------------------------------------
// Float16 helpers (IEEE 754 half-precision)
// ---------------------------------------------------------------------------

/**
 * Convert a JS number to its IEEE 754 half-precision (float16) bit pattern
 * as a 16-bit unsigned integer.
 */
export function floatToFloat16Bits(v: number): number {
  if (isNaN(v)) return 0x7E00;
  if (!isFinite(v)) return v > 0 ? 0x7C00 : 0xFC00;
  if (v === 0) return 0;

  _tmpView.setFloat32(0, v, true);
  const f32 = _tmpView.getUint32(0, true);
  const sign = (f32 >>> 31) & 1;
  const exp32 = (f32 >>> 23) & 0xFF;
  const mantissa32 = f32 & 0x7FFFFF;

  const exp16 = exp32 - 127 + 15;

  if (exp16 <= 0) {
    if (exp16 < -10) return sign << 15;
    const m = ((mantissa32 | 0x800000) + (0x1000 >> (1 - exp16))) >>> (1 - exp16 + 13);
    return (sign << 15) | m;
  }
  if (exp16 >= 31) return (sign << 15) | 0x7C00;

  const rounded = (mantissa32 + 0x1000) >> 13;
  if (rounded >= 0x400) return (sign << 15) | ((exp16 + 1) << 10);
  return (sign << 15) | (exp16 << 10) | rounded;
}

/**
 * Convert an IEEE 754 half-precision bit pattern (uint16) back to a JS number.
 */
export function float16BitsToFloat(bits: number): number {
  const sign = (bits >>> 15) & 1;
  const exp = (bits >>> 10) & 0x1F;
  const mantissa = bits & 0x3FF;

  if (exp === 0) {
    const v = (mantissa / 0x400) * (2 ** -14);
    return sign ? -v : v;
  }
  if (exp === 31) {
    if (mantissa === 0) return sign ? -Infinity : Infinity;
    return NaN;
  }
  const v = (1 + mantissa / 0x400) * (2 ** (exp - 15));
  return sign ? -v : v;
}

// ---------------------------------------------------------------------------
// Float32 monotonic mapping helpers
// ---------------------------------------------------------------------------

/**
 * Map a float32 value to a monotonically-ordered uint32.
 * Positive floats → flip sign bit; negative floats → flip all bits.
 * Result is a 32-bit unsigned integer that preserves the natural ordering of
 * float32 values, so deltas of nearby floats are tiny.
 */
export function float32ToMonotonic(v: number): number {
  _tmpView.setFloat32(0, v, true);
  const u = _tmpView.getUint32(0, true);
  return ((u & 0x80000000) ? (~u) : (u ^ 0x80000000)) >>> 0;
}

/**
 * Inverse of {@link float32ToMonotonic}: restore a float32 from its
 * monotonically-ordered uint32 representation.
 */
export function monotonicToFloat32(m: number): number {
  const u = (m & 0x80000000) ? ((m ^ 0x80000000) >>> 0) : ((~m) >>> 0);
  _tmpView.setUint32(0, u, true);
  return _tmpView.getFloat32(0, true);
}

// ---------------------------------------------------------------------------
// Class discriminator
// ---------------------------------------------------------------------------

/**
 * Read a uint32 class discriminator and resolve it to a constructor.
 * Returns `null` if the hash is 0 or cannot be resolved; the caller should log a warning
 * and skip the property.
 */
export function readClassDiscriminator(reader: BinaryReader): (new (...args: any[]) => any) | null {
  const hash = reader.readUint32();
  if (hash === 0) return null;
  const map = getClassHashMap();
  const fullName = map.get(hash);
  if (!fullName) {
    console.warn(`[ReplicationBinaryCodec] Unknown class hash ${hash} in polymorphic replication payload. Skipping property.`);
    return null;
  }
  try {
    return ClassRegistry.getClass(fullName);
  } catch {
    console.warn(`[ReplicationBinaryCodec] Class "${fullName}" (hash ${hash}) not found in ClassRegistry. Skipping property.`);
    return null;
  }
}
